import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { quotesApi, portsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PortSearch from '../components/PortSearch';
import { formatCurrency, containerTypes, cargoTypes, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { Loader2, ArrowRight } from 'lucide-react';
import { Button, Field, Card, Tag } from '../components/sv';
import { ShipmentModeToggle, BulkCargoFields, useBulkOptions, bulkPayload } from '../components/booking/BulkFields';

export default function QuotePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const bulkOptions = useBulkOptions();
  const [ports, setPorts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [formData, setFormData] = useState({
    origin_port: searchParams.get('origin') || '', destination_port: searchParams.get('destination') || '',
    shipment_mode: searchParams.get('mode') === 'bulk' ? 'bulk' : 'container',
    bulk_cargo_type: 'dry_bulk', bulk_commodity: '', quantity_mt: '', vessel_class: '', loading_terms: 'FIOST',
    cargo_type: 'FCL', container_type: '40ft', container_count: 1, weight: 10000,
  });

  useEffect(function () { portsApi.getAll().then(function (r) { setPorts(r.data); }).catch(function () {}); }, []);
  function handleChange(f, v) { setFormData(function (p) { return { ...p, [f]: v }; }); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!formData.origin_port || !formData.destination_port) { toast.error('Select origin and destination ports'); return; }
    if (formData.shipment_mode === 'bulk' && (!formData.bulk_commodity || Number(formData.quantity_mt) < 500)) { toast.error('Select a bulk commodity and enter at least 500 metric tons'); return; }
    setLoading(true);
    try {
      const res = await quotesApi.getInstant({ ...formData, ...bulkPayload(formData) });
      setQuote(res.data);
    } catch (err) { toast.error('Could not calculate a price'); } finally { setLoading(false); }
  }

  function bookNow() {
    if (isAuthenticated) navigate('/booking', { state: { quote: quote } });
    else navigate('/login', { state: { from: { pathname: '/booking' }, quote: quote } });
  }

  const isBulk = formData.shipment_mode === 'bulk';

  return (
    <div className="bg-gray-50 min-h-screen" data-testid="quote-page">
      <div className="bg-white border-b border-gray-200">
        <div className="sv-wrap py-10 md:py-12">
          <p className="sv-kicker">Prices</p>
          <h1 className="text-[36px] md:text-[40px] sv-display mt-2">Get a quote</h1>
          <p className="text-gray-700 mt-3 max-w-[600px]">Port-to-port freight for containers or bulk tonnage, valid for 7 days. No account required.</p>
        </div>
      </div>

      <div className="sv-wrap py-8 pb-24 grid lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] gap-6 items-start">
        <Card padding="p-8" data-testid="quote-form">
          <form onSubmit={handleSubmit} className="grid gap-6">
            <ShipmentModeToggle value={formData.shipment_mode} onChange={function (v) { handleChange('shipment_mode', v); }} />
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Origin port"><PortSearch ports={ports} value={formData.origin_port} onChange={function (v) { handleChange('origin_port', v); }} placeholder="Port, country or code" testId="origin-port" /></Field>
              <Field label="Destination port"><PortSearch ports={ports} value={formData.destination_port} onChange={function (v) { handleChange('destination_port', v); }} placeholder="Port, country or code" testId="destination-port" /></Field>
            </div>
            <div className="border-t border-gray-200" />
            {isBulk ? (
              <BulkCargoFields formData={formData} onChange={handleChange} options={bulkOptions} />
            ) : (
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Cargo type">
                  <select value={formData.cargo_type} onChange={function (e) { handleChange('cargo_type', e.target.value); }} className="sv-input" data-testid="cargo-type-select">
                    {cargoTypes.map(function (t) { return <option key={t.value} value={t.value}>{t.label}</option>; })}
                  </select>
                </Field>
                <Field label="Container type">
                  <select value={formData.container_type} onChange={function (e) { handleChange('container_type', e.target.value); }} className="sv-input" data-testid="container-type-select">
                    {containerTypes.map(function (t) { return <option key={t.value} value={t.value}>{t.label}</option>; })}
                  </select>
                </Field>
                <Field label="Number of containers"><input type="number" min="1" max="100" value={formData.container_count} onChange={function (e) { handleChange('container_count', parseInt(e.target.value) || 1); }} className="sv-input sv-mono" data-testid="container-count-input" /></Field>
                <Field label="Total weight (kg)"><input type="number" min="1" value={formData.weight} onChange={function (e) { handleChange('weight', parseFloat(e.target.value) || 0); }} className="sv-input sv-mono" data-testid="weight-input" /></Field>
              </div>
            )}
            <div><Button type="submit" size="lg" disabled={loading} data-testid="calculate-btn">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Calculate price'}</Button></div>
          </form>
        </Card>

        {quote ? (
          <div className="sv-card overflow-hidden animate-fadeInUp" data-testid="quote-result">
            <div className="bg-ink text-white p-6 flex items-start justify-between gap-4">
              <div><div className="text-sm text-white/70">Quote</div><div className="sv-mono text-lg mt-0.5" data-testid="quote-number">{quote.quote_number}</div></div>
              <div className="text-right"><div className="text-sm text-white/70">Valid until</div><div className="sv-mono mt-0.5">{formatDate(quote.valid_until)}</div></div>
            </div>
            <div className="p-6 grid gap-5">
              <div className="flex items-center gap-3 text-lg"><span>{quote.origin_port}</span><ArrowRight className="h-4 w-4 text-gray-600" strokeWidth={1.75} /><span>{quote.destination_port}</span></div>
              <div className="flex flex-wrap gap-2">
                <Tag data-testid="quote-cargo-summary">{quote.shipment_mode === 'bulk' ? Number(quote.quantity_mt).toLocaleString() + ' MT ' + String(quote.bulk_commodity).replace(/_/g, ' ') : quote.container_count + ' × ' + quote.container_type + ' · ' + quote.cargo_type}</Tag>
                {quote.shipment_mode === 'bulk' && <Tag><span className="capitalize">{quote.vessel_class}</span> · {quote.loading_terms || 'FIOST'} · ${quote.rate_per_ton}/MT</Tag>}
                <Tag mono>{quote.transit_days} days transit</Tag>
              </div>
              <div className="border-t border-gray-200 pt-4 grid gap-2 text-[15px]">
                <div className="flex justify-between"><span className="text-gray-700">Ocean freight</span><span className="sv-mono">{formatCurrency(quote.base_price)}</span></div>
                <div className="flex justify-between"><span className="text-gray-700">Fuel surcharge</span><span className="sv-mono">{formatCurrency(quote.fuel_surcharge)}</span></div>
                <div className="flex justify-between"><span className="text-gray-700">{quote.shipment_mode === 'bulk' ? 'Port charges and stevedoring' : 'Terminal handling'}</span><span className="sv-mono">{formatCurrency(quote.handling_fee)}</span></div>
                <div className="flex justify-between items-baseline border-t border-gray-200 pt-3 mt-1"><span>Total</span><span className="text-[28px] font-light sv-mono" data-testid="quote-total">{formatCurrency(quote.total_price)}</span></div>
              </div>
              <Button size="lg" onClick={bookNow} className="w-full" data-testid="book-now-btn">Book this quote <ArrowRight className="h-[18px] w-[18px]" strokeWidth={1.75} /></Button>
              <p className="text-[13px] text-gray-600">Excludes local charges at origin and destination, customs and insurance unless selected at booking.</p>
            </div>
          </div>
        ) : (
          <Card data-testid="quote-placeholder">
            <p className="text-lg">Your price appears here</p>
            <p className="text-gray-700 mt-2 text-[15px]">Choose a route and cargo details to see ocean freight, surcharges and transit time. Prices follow the published tariff and are fixed for 7 days.</p>
            <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
              {[['7 days', 'price validity'], ['2 min', 'to a firm price'], ['0', 'account needed']].map(function (s) { return <div key={s[1]}><div className="text-2xl font-light">{s[0]}</div><div className="text-[13px] text-gray-600">{s[1]}</div></div>; })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
